import { computed, ref, Ref } from 'vue'
import { Employee } from '../../employees/models/employee'
import { Area, AreaID } from '../../areas/models/area'

export type EmployeeSelectDialogState = {
    areaId: AreaID | null
    employees: Employee[]
    visible: boolean
    resolve?: (value: Employee[] | undefined) => void
    reject?: () => void
}

const state: Ref<EmployeeSelectDialogState> = ref({
    areaId: null,
    employees: [],
    visible: false,
})

export function useEmployeeSelectDialog() {
    const show = async (data: { employees: Employee[], area: Area }): Promise<Employee[] | undefined> => {
        return new Promise((resolve, reject) => {
            state.value = {
                areaId: data.area.id,
                employees: data.employees,
                visible: true,
                resolve,
                reject
            }
        })
    }

    const hide = () => {
        state.value.resolve?.(undefined)
        resetState()
    }

    const confirm = (selectedEmployees: Employee[]) => {
        state.value.resolve?.(selectedEmployees)
        resetState()
    }

    const cancel = () => {
        state.value.reject?.()
        resetState()
    }

    const resetState = () => {
        state.value = {
            areaId: null,
            employees: [],
            visible: false,
        }
    }

    const addEmployees = (employees: Employee[]) => {
        state.value.employees = [...state.value.employees, ...employees]
    }

    const removeEmployee = (employee: Employee) => {
        state.value.employees = state.value.employees.filter((e) => e.id !== employee.id)
    }

    const removeAll = () => {
        state.value.employees = []
    }

    const isShown = computed(() => state.value.visible)

    const employees = computed(() => state.value.employees)

    const areaId = computed(() => state.value.areaId)

    return {
        show,
        hide,
        confirm,
        cancel,
        addEmployees,
        removeEmployee,
        removeAll,
        isShown,
        employees,
        areaId,
    }
}